import React, { useState, useEffect, useRef, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import Report from '../../../Report/Report';

const arraysEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const DashboardTable = ({
    headerName,
    loading,
    data,
    metrics,
    keysMetric,
    rowsCount,
    getLastData,
    clear,
    t
}) => {
    const [state, setState] = useState({
        headerName: headerName,
        loading: loading,
        data: data,
        metrics: metrics,
        keysMetric: keysMetric,
        isTableLast: true
    });

    const bodyRef = useRef(null);

    const setBodyData = useCallback((dataString) => {
        if (!bodyRef.current) {
            return;
        }

        const html = dataString !== undefined ? dataString : state.data;
        bodyRef.current.innerHTML = html || '';
    }, [state.data]);

    useEffect(() => {
        clear();
        getLastData().catch(err => {
            console.warn('DashboardTable componentDidMount error:', err);
        });
    }, [getLastData, clear]);

    useEffect(() => {
        setState(prevState => {
            const isChangeData = data !== prevState.data;
            const headerChanged = headerName !== prevState.headerName;
            const metricsChanged = metrics !== prevState.metrics;
            const keysChanged = !arraysEqual(keysMetric, prevState.keysMetric);
            const structureChanged = headerChanged || metricsChanged || keysChanged;
            const hasContent = typeof prevState.data === 'string' && prevState.data.trim().length > 0;
            const shouldLockStructure = loading && structureChanged && !isChangeData;
            const shouldPreserveContent = loading && hasContent && !structureChanged;

            const nextState = {
                ...prevState,
                data: shouldLockStructure || shouldPreserveContent ? prevState.data : (loading ? '' : data),
                headerName: shouldLockStructure || shouldPreserveContent ? prevState.headerName : headerName,
                loading: loading,
                metrics: shouldLockStructure || shouldPreserveContent ? prevState.metrics : metrics,
                keysMetric: shouldLockStructure || shouldPreserveContent ? prevState.keysMetric : keysMetric
            };

            if (shouldLockStructure || shouldPreserveContent) {
                return nextState;
            }

            if (loading) {
                bodyRef.current && (bodyRef.current.innerHTML = '');
            } else if (isChangeData) {
                setBodyData(data);
            }

            return nextState;
        });
    }, [data, headerName, loading, metrics, keysMetric, setBodyData]);

    useEffect(() => {
        return () => {
            clear();
        };
    }, [clear]);

    return (
        <Report
            headerName={state.headerName}
            loading={state.loading}
            data={state.data}
            metrics={state.metrics}
            keysMetric={state.keysMetric}
            rowsCount={rowsCount}
            isSorting={false}
            isTableLast={state.isTableLast}
            tableWrapperClass={{
                'table-responsive_compact-report': true,
                'table-responsive_compact-report_dashboard': true
            }}
            t={t}
        />
    );
};

DashboardTable.propTypes = {
    headerName: PropTypes.array,
    loading: PropTypes.bool,
    data: PropTypes.string,
    metrics: PropTypes.string,
    keysMetric: PropTypes.array,
    rowsCount: PropTypes.number,
    getLastData: PropTypes.func,
    clear: PropTypes.func,
    t: PropTypes.object
};

DashboardTable.defaultProps = {
    headerName: [],
    loading: false,
    data: '',
    metrics: '',
    keysMetric: [],
    rowsCount: 0,
    getLastData: () => {},
    clear: () => {}
};

export default withTranslation(DashboardTable);
